import { useEffect, useState } from "react";

import { useChat } from "../hooks/useChat";
import { useTheme } from "../hooks/useTheme";
import { Avatar } from "./Avatar";
import { Composer } from "./Composer";
import { MessageList } from "./MessageList";
import { ThemeSwitcher } from "./ThemeSwitcher";
import styles from "./ChatWidget.module.css";

/** Turn a one-tap chip into a clear message for the model.
 *
 * This advisor covers gaming laptops only now — the backend never sends any
 * other use-case chip any more (see app/aero/service.py's
 * _quick_replies_for), so there is nothing to map here besides Yes/No.
 */
const CHOICE_MESSAGE: Record<string, string> = {
  // Deliberately NOT "Yes"/"No" here: those two chips are reused across many
  // different yes/no questions (which game/app is in mind, raise the budget,
  // show options at the entry price, ...), each meaning something different.
  // A single fixed expansion for "Yes" was previously sent for ALL of them —
  // "Yes, I have a specific one in mind." even when the actual question was
  // "shall I show you what's available from ₹120,000?" — which the backend
  // then failed to recognise as an answer to THAT question at all (reported
  // live). The literal "Yes"/"No" is unambiguous on its own and is exactly
  // what every one of these flows already matches against server-side, so
  // falling through to the bare label (see the `?? label` below) is correct
  // for all of them at once, with no per-question special-casing.
};

export function ChatWidget() {
  const { theme, name, setName } = useTheme();

  // Closed on load, so the page it sits on is the first thing the visitor
  // sees and the assistant is there when they want it. Reported in review:
  // "Starting Toggle Needs to be minimized rather than open". The embeddable
  // widget (app/widget/aero-widget.js) already starts closed; this is the
  // React app catching up with it.
  const [open, setOpen] = useState(false);
  const [maximised, setMaximised] = useState(false);
  // `active: open` is what makes the greeting type itself WHEN THE PANEL
  // OPENS rather than when the page loads. See UseChatOptions.
  const { messages, sending, streaming, status, send, reset } = useChat({
    active: open,
  });
  const busy = sending || streaming;

  // Freeze the page behind the panel whenever it covers the screen — always on
  // phones, or on any device when the user has maximised it.
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 600px)");
    const sync = () => {
      const covers = open && (maximised || phone.matches);
      document.body.style.overflow = covers ? "hidden" : "";
    };
    sync();
    phone.addEventListener("change", sync);
    return () => {
      phone.removeEventListener("change", sync);
      document.body.style.overflow = "";
    };
  }, [open, maximised]);

  // Leaving maximised state is meaningless once the panel is closed.
  useEffect(() => {
    if (!open) setMaximised(false);
  }, [open]);

  return (
    <div className={styles.root} style={theme.vars}>
      <ThemeSwitcher current={name} onChange={setName} />

      <div
        className={[
          styles.dock,
          open ? "" : styles.dockClosed,
          open && maximised ? styles.dockMax : "",
        ].join(" ")}
      >
        {open ? (
          <div className={`${styles.panelShell} ${maximised ? styles.panelShellMax : ""}`}>
            <section
              className={`${styles.panel} ${maximised ? styles.panelMax : ""}`}
              aria-label="Alienware Arena Assistant"
            >
              {/* Two lines and nothing else: who this is, and what it does
                  for you. No "AI" tag — it says nothing a customer wants —
                  and no blinking "ONLINE" dot, which was a status claim
                  dressed up as decoration. .actions keeps the controls
                  right-aligned on its own margin-left:auto. */}
              {/* Two rows. The top one carries the Alienware mark, the name
                  and the tagline; the attribution gets a strip of its own
                  underneath.
                  
                  The strip is not decoration, it is what makes the row
                  above fit. Measured, not eyeballed: the panel is 392px and
                  the controls take 116px, so putting "Powered by
                  CoreOps.AI" on the same line as the name leaves the name
                  less room than it already needs. On its own full-width
                  line it costs nothing horizontally. */}
              <header className={styles.header}>
                <div className={styles.headTop}>
                  {/* The Alienware mark — alienware.png painted through a
                      mask in the theme's accent, so it follows the theme
                      switcher. See Avatar.module.css. */}
                  <Avatar size={36} eyes thinking={busy} />
                  <div className={styles.brand}>
                    <div className={styles.name}>{theme.botName}</div>
                    <div className={styles.tagline}>{theme.botRole}</div>
                  </div>
                  <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.textBtn}
                    onClick={reset}
                    title="Start a new conversation"
                  >
                    NEW
                  </button>
                  <button
                    type="button"
                    className={`${styles.iconBtn} ${styles.maxBtn}`}
                    onClick={() => setMaximised((m) => !m)}
                    aria-pressed={maximised}
                    aria-label={maximised ? "Minimise chat" : "Maximise chat"}
                    title={maximised ? "Minimise" : "Maximise"}
                  >
                    {maximised ? "🗗" : "⛶"}
                  </button>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={() => setOpen(false)}
                    aria-label="Close chat"
                    title="Close chat"
                  >
                    –
                  </button>
                  </div>
                </div>
                <div className={styles.poweredBy}>
                  Powered by{" "}
                  <a
                    className={styles.poweredByBrand}
                    href="https://www.coreops.ai/"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    CoreOps.AI
                  </a>
                </div>
              </header>

              <MessageList
                messages={messages}
                sending={sending}
                busy={busy}
                status={status}
                onAction={send}
                onChoice={(label) => send(CHOICE_MESSAGE[label] ?? label)}
                onBudget={(inr) =>
                  send(`My budget is about ₹${inr.toLocaleString("en-IN")}.`, {
                    budgetInr: inr,
                  })
                }
              />

              <Composer onSend={send} disabled={busy} />
            </section>
          </div>
        ) : (
          <button
            type="button"
            className={styles.launcher}
            onClick={() => setOpen(true)}
          >
            {/* Written out in full: "spec" was a short form, and "holds it"
                left the reader to work out what "it" was. This is the first
                thing anyone reads, before they have any idea what the
                assistant does. */}
            <span className={styles.launcherLabel}>
              Want a specification that actually runs your games?{" "}
              <b>Ask {theme.botName}</b>
            </span>
            <span className={styles.launcherAvatar}>
              <Avatar size={60} pulsing eyes />
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
